import React from 'react';

import { Typography } from '@mui/material';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

import type { OutlineHeadingItem, OutlineMenuViewProps } from './types';

const headingBase = {
  display: 'inline-block',
  flexShrink: 0,
  fontSize: '12px',
  maxWidth: '100%',
} as const;

const useStyles = makeStyles()(() => ({
  activeItem: {
    boxShadow: 'inset 1px 0 0 0 #146EE8',
    color: '#146EE8',
  },
  heading1: {
    ...headingBase,
    paddingLeft: '10px',
  },
  heading2: {
    ...headingBase,
    paddingLeft: '26px',
  },
  heading3: {
    ...headingBase,
    paddingLeft: '42px',
  },
  label: {
    lineHeight: '18px',
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  list: {
    boxShadow: 'inset 1px 0 0 0 #D1D1D1',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    listStyleType: 'none',
    margin: 0,
    marginLeft: '10px',
    paddingLeft: 0,
    width: 'calc(100% - 14px)',
  },
  listButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'inherit',
    cursor: 'pointer',
    font: 'inherit',
    height: '18px',
    paddingBottom: 0,
    paddingRight: 0,
    paddingTop: 0,
    width: '100%',
  },
  listItem: {
    '&:hover': {
      color: '#146EE8',
    },
    height: '18px',
    lineHeight: '18px',
  },
  placeholder: {
    color: '#324A52',
    fontSize: '12px',
    paddingLeft: '10px',
  },
  root: {
    height: 'calc(100% - 36px)',
    marginTop: '8px',
  },
}));

export const HeadingsList: React.FC<
  Pick<OutlineMenuViewProps, 'handleScrollTo' | 'selectedHeadingIndex'> & {
    headings: OutlineHeadingItem[] | null;
  }
> = ({ handleScrollTo, headings, selectedHeadingIndex }) => {
  const { classes } = useStyles();

  if (!headings) return null;

  return (
    <div className={classes.root}>
      {headings.length ? (
        <ul className={classes.list}>
          {headings.map((heading, index) => (
            <li
              key={index}
              className={clsx(classes.listItem, {
                [classes.activeItem]: index === selectedHeadingIndex,
              })}
            >
              <button
                className={clsx(classes.listButton, {
                  [classes.heading1]: heading.variant === 'style-heading1',
                  [classes.heading2]: heading.variant === 'style-heading2',
                  [classes.heading3]: heading.variant === 'style-heading3',
                })}
                type="button"
                onClick={() => handleScrollTo(heading)}
              >
                <Typography className={classes.label} title={heading.label}>
                  {heading.label}
                </Typography>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <Typography className={classes.placeholder}>No headings</Typography>
      )}
    </div>
  );
};
